/**
 * VERA Header Component
 * Top navigation bar matching LensLock portal design language
 */
import React from "react";
import {
  makeStyles,
  tokens,
  Text,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  Divider,
  Badge,
} from "@fluentui/react-components";
import {
  SignOut24Regular,
  Shield24Filled,
} from "@fluentui/react-icons";
import { useAuth } from "../../auth/AuthProvider";

const useStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "60px",
    padding: `0 ${tokens.spacingHorizontalXL}`,
    background: "linear-gradient(135deg, #004C97 0%, #003a75 100%)",
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 76, 151, 0.25)",
    zIndex: 100,
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
  },
  logoContainer: {
    backgroundColor: "#ffffff",
    padding: "6px 12px",
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
  },
  logoImage: {
    height: "36px",
    width: "auto",
  },
  logoTextContainer: {
    display: "flex",
    flexDirection: "column",
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  logoText: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#ffffff",
    letterSpacing: "3px",
    fontFamily: "'Poppins', sans-serif",
  },
  veraBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    padding: "2px 10px",
    borderRadius: "4px",
    fontSize: "10px",
    fontWeight: 600,
    color: "rgba(255, 255, 255, 0.9)",
    letterSpacing: "1px",
    textTransform: "uppercase" as const,
    fontFamily: "'Poppins', sans-serif",
  },
  logoSubtext: {
    fontSize: "11px",
    color: "rgba(255, 255, 255, 0.7)",
    letterSpacing: "0.3px",
    marginTop: "-2px",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginRight: tokens.spacingHorizontalS,
  },
  userName: {
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
  },
  userRole: {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "12px",
  },
  avatarButton: {
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  },
});

export const Header: React.FC = () => {
  const styles = useStyles();
  const { displayName, username, logout } = useAuth();

  const userName = displayName || username || "User";
  const userInitials = userName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  return (
    <header className={styles.header}>
      <div className={styles.logo}>
        {/* LensLock Logo in white container */}
        <div className={styles.logoContainer}>
          <img
            src="/lenslock_logo_color-RGB-1pt-stroke-wht.png"
            alt="LensLock"
            className={styles.logoImage}
          />
        </div>
        <div className={styles.logoTextContainer}>
          <div className={styles.brandRow}>
            <Text className={styles.logoText}>Arroyo360</Text>
            <div className={styles.veraBadge}>
              <Shield24Filled style={{ fontSize: "12px" }} />
              Evidence Portal
            </div>
          </div>
          <Text className={styles.logoSubtext}>
            Virtual Evidence & Reconstruction Archive by LensLock
          </Text>
        </div>
      </div>

      <div className={styles.userSection}>
        <div className={styles.userInfo}>
          <Text className={styles.userName}>{userName}</Text>
          <Text className={styles.userRole}>Authorized User</Text>
        </div>

        <Menu>
          <MenuTrigger>
            <Button
              appearance="transparent"
              icon={
                <Avatar
                  name={userName}
                  initials={userInitials}
                  size={36}
                  color="brand"
                />
              }
              className={styles.avatarButton}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem icon={<SignOut24Regular />} onClick={logout}>
                Sign Out
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </header>
  );
};

export default Header;
